/**
 * Lightweight orchestrator for pinned clients management
 * Coordinates specialized hooks and provides unified API
 */

import usePortfolioState from './portfolio/usePortfolioState';
import usePortfolioViews from './portfolio/usePortfolioViews';
import usePortfolioActions from './portfolio/usePortfolioActions';
import useClientLocking from './client-interactions/useClientLocking';
import useCardFlipping from './client-interactions/useCardFlipping';
import useTableExpansion from './client-interactions/useTableExpansion';
import useInteractionCleanup from './portfolio/useInteractionCleanup';
import usePortfolioOrchestrator from './portfolio/usePortfolioOrchestrator';
import usePortfolioValidation from './portfolio/usePortfolioValidation';

/**
 * Custom hook for managing pinned clients
 * @returns {Object} Pinned clients state and utility functions
 */
const usePinnedClients = (filterPinnedClientsByYear) => {
  // Core portfolio hooks
  const portfolioState = usePortfolioState();
  const portfolioViews = usePortfolioViews();
  const portfolioActions = usePortfolioActions(
    portfolioState.pinnedClients,
    portfolioState.orderedPinnedClients,
    portfolioState.setPinnedClients,
    portfolioState.setOrderedPinnedClients,
    portfolioViews.isHardPinned
  );

  // Interaction hooks
  const clientLocking = useClientLocking();
  const cardFlipping = useCardFlipping();
  const tableExpansion = useTableExpansion();

  // Interaction states and setters
  const interactionStates = {
    lockedClients: clientLocking.lockedClients,
    flippedCards: cardFlipping.flippedCards,
    expandedTableRow: tableExpansion.expandedTableRow
  };

  const interactionSetters = {
    setLockedClients: clientLocking.setLockedClients,
    setFlippedCards: cardFlipping.setFlippedCards,
    setExpandedTableRow: tableExpansion.setExpandedTableRow
  };

  // Cleanup utilities
  const cleanupUtils = useInteractionCleanup(
    interactionStates,
    interactionSetters,
    portfolioState.getClientKey
  );

  // Portfolio orchestration
  const portfolioOrchestrator = usePortfolioOrchestrator(
    {
      ...portfolioState,
      ...portfolioViews,
      ...portfolioActions
    },
    { ...cleanupUtils, ...interactionStates, getClientKey: portfolioState.getClientKey }
  );

  // Portfolio validation
  const portfolioValidation = usePortfolioValidation(
    {
      pinnedClients: portfolioState.pinnedClients,
      pinnedViewType: portfolioViews.pinnedViewType,
      getClientKey: portfolioState.getClientKey
    },
    cleanupUtils
  );

  // Handle reordering of pinned clients
  const handleReorderPinnedClients = (reorderedClients) => {
    portfolioState.setOrderedPinnedClients(reorderedClients);
  };

  // Apply year filtering to pinned clients if filter function is provided
  const filteredPinnedClients = filterPinnedClientsByYear 
    ? filterPinnedClientsByYear(portfolioState.pinnedClients)
    : portfolioState.pinnedClients;
    
  const filteredOrderedPinnedClients = filterPinnedClientsByYear 
    ? filterPinnedClientsByYear(portfolioState.orderedPinnedClients)
    : portfolioState.orderedPinnedClients;

  return {
    // State - return filtered versions when year filter is active
    pinnedClients: filteredPinnedClients,
    orderedPinnedClients: filteredOrderedPinnedClients.length > 0 
      ? filteredOrderedPinnedClients 
      : filteredPinnedClients,
    pinnedViewType: portfolioViews.pinnedViewType,
    portfolioType: portfolioViews.portfolioType,
    activeManager: portfolioViews.activeManager,
    isHardPinned: portfolioViews.isHardPinned,
    useUniformColor: portfolioViews.useUniformColor,
    
    // Core methods
    isPinned: portfolioState.isPinned,
    togglePinClient: portfolioOrchestrator.enhancedTogglePinClient,
    unpinClient: portfolioOrchestrator.enhancedUnpinClient,
    unpinAllClients: portfolioOrchestrator.enhancedUnpinAllClients,
    toggleViewType: portfolioViews.toggleViewType,
    handleReorderPinnedClients,
    toggleHardPin: portfolioViews.toggleHardPin,
    pinManagerClients: portfolioOrchestrator.enhancedPinManagerClients,
    pinTierClients: portfolioActions.pinTierClients,
    pinCountryClients: portfolioActions.pinCountryClients,
    unpinCountryClients: portfolioOrchestrator.enhancedUnpinCountryClients,
    toggleUniformColor: portfolioViews.toggleUniformColor,
    
    // State setters for direct manipulation
    setPinnedClients: portfolioState.setPinnedClients,
    setOrderedPinnedClients: portfolioState.setOrderedPinnedClients,
    
    // Interaction methods
    isClientLocked: clientLocking.isClientLocked,
    toggleClientLock: clientLocking.toggleClientLock,
    setLockedClients: clientLocking.setLockedClients,
    isCardFlipped: cardFlipping.isCardFlipped,
    toggleCardFlip: cardFlipping.toggleCardFlip,
    isTableRowExpanded: tableExpansion.isTableRowExpanded,
    toggleTableRowExpansion: tableExpansion.toggleTableRowExpansion,
    
    // Validation methods
    canPinClient: portfolioValidation.canPinClient,
    canUnpinClient: portfolioValidation.canUnpinClient,
    validatePortfolioState: portfolioValidation.validatePortfolioState
  };
};

export default usePinnedClients;
